import React, { useState } from "react";
import {
  Button,
  TableRow,
  Table,
  TableHead,
  TableBody,
  Typography,
  TableCell,
  Card,
  Box,
  Grid,
  TableContainer,
  Paper,
  Chip,
} from "@material-ui/core";
import { withStyles, makeStyles } from "@material-ui/core/styles";
import GetAppIcon from "@material-ui/icons/GetApp";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import EventService from "./Locality/Service/eventService";
import StudentService from "./Locality/Service/studentService";
import PageTitle from "../../components/PageTitle/PageTitle";
import Widget from "../../components/Widget/Widget";
import StudentCards from "./UserProfileCard";

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: "#0f3b62ad",
    color: theme.palette.common.white,
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

const useStyles = makeStyles((theme) => ({
  tableOverflow: {
    overflow: "auto",
  },
}));

const normalizeStatus = (value) => String(value || "").trim().toLowerCase();

const isSubmittedStatus = (status) => normalizeStatus(status) === "submitted";

const isResubmitRequested = (resubmit) => {
  const r = normalizeStatus(resubmit);
  return r === "resubmit" || r === "submitted";
};

/** Parent requested resubmit — allow upload even after due date */
const MAX_UPLOAD_FILES_AT_A_TIME = 10;
const isResubmitPendingUpload = (resubmit) => normalizeStatus(resubmit) === "resubmit";

const getSubmissionStatusStyle = (status) => {
  const s = normalizeStatus(status);
  if (s === "submitted") {
    return { backgroundColor: "#e8f5e9", color: "#2e7d32" };
  }
  return { backgroundColor: "#fff3e0", color: "#e65100" };
};

const getResubmitStatusStyle = (resubmit) => {
  const r = normalizeStatus(resubmit);
  if (r === "resubmit") {
    return { backgroundColor: "#fff8e1", color: "#f57f17" };
  }
  if (r === "submitted") {
    return { backgroundColor: "#e3f2fd", color: "#1565c0" };
  }
  return { backgroundColor: "#f5f5f5", color: "#616161" };
};

/** Parse YYYY-MM-DD or DD/MM/YYYY → display DD/MM/YYYY */
const formatDateDMY = (date) => {
  if (!date) return "";
  const s = String(date).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [year, month, day] = s.slice(0, 10).split("-");
    return `${day}/${month}/${year}`;
  }
  const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    const [, d, m, y] = slash;
    return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
  }
  return s;
};

const isDueDatePassed = (dueDateValue) => {
  if (!dueDateValue) return false;
  const s = String(dueDateValue).trim();
  let y;
  let m;
  let d;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    [y, m, d] = s.slice(0, 10).split("-");
  } else {
    const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!slash) return false;
    [, d, m, y] = slash;
  }
  const due = new Date(Number(y), Number(m) - 1, Number(d), 23, 59, 59, 999);
  if (Number.isNaN(due.getTime())) return false;
  return new Date() > due;
};

const resolveClassId = (classRef) => {
  if (!classRef) return "";
  if (typeof classRef === "object") return classRef._id || "";
  return String(classRef);
};

const getFileName = (url) => {
  try {
    const path = (url || "").split("/").pop() || url;
    return (path || "").split("?")[0] || "document";
  } catch {
    return "document";
  }
};

const downloadFromUrl = async (url, filename) => {
  if (!url) return;
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.setAttribute("download", filename || getFileName(url));
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch {
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename || getFileName(url));
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export default function Assignment() {
  const classes = useStyles();
  const [eventList, setEventList] = useState([]);
  const [studentList, setStudentList] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [selectedClassId, setSelectedClassId] = useState("");

  const mapEventRow = (event) => {
    const details = event.eventStudentDetailsListId;
    const studentDetails = Array.isArray(details) ? details[0] || {} : details || {};
    const rawDate = event.date || "";
    const rawDueDate = event.dueDate || "";
    const submissionFiles = Array.isArray(studentDetails.submissionFiles)
      ? studentDetails.submissionFiles.filter(Boolean)
      : [];
    const teacherFiles = Array.isArray(event.imageURL)
      ? event.imageURL.filter(Boolean)
      : event.imageURL
      ? [event.imageURL]
      : [];

    return {
      ...event,
      eventStudentDetailsListId: studentDetails,
      teacherFiles,
      submissionFiles,
      rawDate,
      rawDueDate,
      assignmentDateDisplay: rawDate ? formatDateDMY(rawDate) : "—",
      dueDateDisplay: rawDueDate ? formatDateDMY(rawDueDate) : "—",
      dueDateReached: event.dueDateReached === true || isDueDatePassed(rawDueDate),
      hasParentUpload:
        isSubmittedStatus(studentDetails.status) || submissionFiles.length > 0,
    };
  };

  const getEventList = (studentId, classId) => {
    if (!studentId) return;
    setSelectedStudentId(studentId);
    setSelectedClassId(classId || "");
    EventService.findEventList({ studentId, classId })
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        setEventList(list.map(mapEventRow));
      })
      .catch((err) => {
        console.error(err);
        setEventList([]);
      });
  };

  const fetchStudentList = () => {
    const userDetails = JSON.parse(localStorage.getItem("userDetail") || "{}");
    StudentService.findStudentList({ userId: userDetails._id })
      .then((res) => {
        const mappedStudents = (res || []).map((student) => ({
          ...student,
          fullName: `${student.firstName || ""} ${student.lastName || ""}`.trim(),
        }));
        setStudentList(mappedStudents);
        if (mappedStudents.length > 0) {
          const first = mappedStudents[0];
          getEventList(first._id, resolveClassId(first.classId));
        }
      })
      .catch((err) => console.error(err));
  };

  React.useEffect(() => {
    fetchStudentList();
  }, []);

  const handleStudentClick = (studentId, classId) => {
    const student = studentList.find((s) => s._id === studentId);
    const resolvedClassId =
      classId || resolveClassId(student?.classId);
    getEventList(studentId, resolvedClassId);
  };

  const refreshList = () => {
    if (selectedStudentId) {
      getEventList(selectedStudentId, selectedClassId);
    }
  };

  const onRequestResubmit = (event) => {
    const detailId = event?.eventStudentDetailsListId?._id;
    if (!detailId) {
      alert("Assignment details not found for this student.");
      return;
    }
    EventService.upadeStudentEvent({
      _id: detailId,
      resubmit: "Resubmit",
      status: event.eventStudentDetailsListId?.status || "Not Submitted",
    })
      .then(() => {
        alert("Resubmission requested successfully.");
        refreshList();
      })
      .catch((err) => {
        alert(err?.response?.data?.message || "Failed to request resubmission.");
      });
  };

  const handleParentFileChange = async (e, event) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const detailId = event?.eventStudentDetailsListId?._id;
    if (files.length === 0 || !detailId) return;

    if (files.length > MAX_UPLOAD_FILES_AT_A_TIME) {
      alert(`You can upload at most ${MAX_UPLOAD_FILES_AT_A_TIME} files at a time.`);
      if (e?.target) e.target.value = "";
      return;
    }

    const resubmit = event?.eventStudentDetailsListId?.resubmit;
    if (event?.dueDateReached && !isResubmitPendingUpload(resubmit)) {
      alert("Due date is passed. Upload is disabled.");
      if (e?.target) e.target.value = "";
      return;
    }
    if (event?.hasParentUpload && !isResubmitPendingUpload(resubmit)) {
      alert("You have already uploaded documents for this assignment.");
      if (e?.target) e.target.value = "";
      return;
    }

    try {
      // Backend accepts one file per request, so upload each selected file in parallel.
      const uploadResults = await Promise.all(
        files.map((file) => {
          const formData = new FormData();
          formData.append("image", file);
          formData.append("imageName", file.name);
          return EventService.uploadImage(formData);
        })
      );

      const urls = uploadResults.map((res) => res?.url).filter(Boolean);
      if (urls.length === 0) {
        alert("Failed to upload document.");
        return;
      }

      const existing = event.submissionFiles || [];
      const updatedFiles = [...existing, ...urls];

      await EventService.upadeStudentEvent({
        _id: detailId,
        submissionFiles: updatedFiles,
        status: "Submitted",
      });

      alert(
        urls.length > 1
          ? `${urls.length} documents uploaded successfully.`
          : "Document uploaded successfully."
      );
      refreshList();
    } catch (err) {
      console.error("Parent document upload failed", err);
      alert(err?.response?.data?.message || "Failed to upload document.");
    } finally {
      if (e?.target) e.target.value = "";
    }
  };

  const sortEvents = (list) =>
    [...list].sort((a, b) => {
      const da = a.rawDate ? new Date(a.rawDate).getTime() : 0;
      const db = b.rawDate ? new Date(b.rawDate).getTime() : 0;
      if (db !== da) return db - da;
      return String(b.eventName || "").localeCompare(String(a.eventName || ""));
    });

  return (
    <>
      <PageTitle title="Assignment" />

      <Card>
        {studentList.length > 1 && (
          <Box style={{ margin: 15 }}>
            <StudentCards
              studentList={studentList}
              onStudentSelect={handleStudentClick}
              selectedStudentId={selectedStudentId}
            />
          </Box>
        )}
      </Card>
      <br />

      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Widget upperTitle noBodyPadding bodyClass={classes.tableOverflow}>
            <TableContainer component={Paper} style={{ maxHeight: 500 }}>
              <Table stickyHeader className="mb-0">
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Assignment</StyledTableCell>
                    <StyledTableCell>Assignment Date</StyledTableCell>
                    <StyledTableCell>Last Date for Submission</StyledTableCell>
                    <StyledTableCell>Assignment Document</StyledTableCell>
                    <StyledTableCell>Your Upload</StyledTableCell>
                    <StyledTableCell>Remarks</StyledTableCell>
                    <StyledTableCell>Status</StyledTableCell>
                    <StyledTableCell>Request Resubmission</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {eventList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No assignments found for this student.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortEvents(eventList).map((event) => {
                      const detail = event.eventStudentDetailsListId || {};
                      const resubmitStatus = detail.resubmit || "Not Submitted";
                      const submissionStatus = detail.status || "Not Submitted";
                      const isPastDue = event.dueDateReached === true;
                      const canRequestResubmit =
                        isPastDue && !isResubmitRequested(resubmitStatus);
                      const resubmitUploadAllowed = isResubmitPendingUpload(resubmitStatus);
                      const alreadyUploaded = event.hasParentUpload && !resubmitUploadAllowed;
                      const uploadDisabled =
                        alreadyUploaded || (isPastDue && !resubmitUploadAllowed);

                      return (
                        <TableRow key={event._id}>
                          <TableCell>
                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                              {event.eventName || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>{event.assignmentDateDisplay}</TableCell>
                          <TableCell
                            style={{
                              color: isPastDue ? "#c62828" : "inherit",
                              fontWeight: isPastDue ? 600 : "normal",
                            }}
                          >
                            {event.dueDateDisplay}
                            {isPastDue && (
                              <Chip
                                size="small"
                                label="Due date passed"
                                style={{
                                  marginLeft: 8,
                                  backgroundColor: "#ffebee",
                                  color: "#c62828",
                                  fontWeight: 600,
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {event.teacherFiles.length === 0 ? (
                              <Typography variant="caption" color="textSecondary">
                                No file
                              </Typography>
                            ) : (
                              <Box display="flex" flexDirection="column" style={{ gap: 4 }}>
                                {event.teacherFiles.map((url, idx) => (
                                  <Button
                                    key={`teacher-${event._id}-${idx}`}
                                    size="small"
                                    startIcon={<GetAppIcon style={{ fontSize: 18 }} />}
                                    onClick={() =>
                                      downloadFromUrl(url, getFileName(url))
                                    }
                                    style={{
                                      color: "#0f3b62",
                                      justifyContent: "flex-start",
                                      textTransform: "none",
                                    }}
                                  >
                                    Download
                                  </Button>
                                ))}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box display="flex" flexDirection="column" style={{ gap: 6 }}>
                              {event.hasParentUpload && (
                                <>
                                  {event.submissionFiles.map((url, idx) => (
                                    <Button
                                      key={`sub-${event._id}-${idx}`}
                                      size="small"
                                      startIcon={<GetAppIcon style={{ fontSize: 18 }} />}
                                      onClick={() =>
                                        downloadFromUrl(
                                          url,
                                          `my-submission-${idx + 1}-${getFileName(url)}`,
                                        )
                                      }
                                      style={{
                                        color: "#2e7d32",
                                        justifyContent: "flex-start",
                                        textTransform: "none",
                                      }}
                                    >
                                      Download upload
                                    </Button>
                                  ))}
                                  {!resubmitUploadAllowed && (
                                    <Chip
                                      size="small"
                                      label="Submitted"
                                      style={{
                                        width: "fit-content",
                                        backgroundColor: "#e8f5e9",
                                        color: "#2e7d32",
                                      }}
                                    />
                                  )}
                                </>
                              )}
                              {/* {alreadyUploaded && (
                                <Typography variant="caption" style={{ color: "#6b7280" }}>
                                  Upload disabled — document already submitted.
                                </Typography>
                              )} */}
                              <input
                                id={`parent-upload-${event._id}`}
                                type="file"
                                accept="image/*,application/pdf,.doc,.docx"
                                multiple
                                style={{ display: "none" }}
                                disabled={uploadDisabled}
                                onChange={(e) => handleParentFileChange(e, event)}
                              />
                              <label htmlFor={`parent-upload-${event._id}`}>
                                <Button
                                  size="small"
                                  component="span"
                                  startIcon={<CloudUploadIcon style={{ fontSize: 18 }} />}
                                  disabled={uploadDisabled}
                                  style={{
                                    color: uploadDisabled ? "#9e9e9e" : "#0f3b62",
                                    textTransform: "none",
                                  }}
                                >
                                  Upload Document
                                </Button>
                              </label>
                            </Box>
                          </TableCell>
                          <TableCell>{event.discription || "—"}</TableCell>
                          <TableCell>
                            <Box display="flex" flexDirection="column" style={{ gap: 6 }}>
                              <Chip
                                size="small"
                                label={submissionStatus}
                                style={{
                                  width: "fit-content",
                                  fontWeight: 600,
                                  ...getSubmissionStatusStyle(submissionStatus),
                                }}
                              />
                              <Chip
                                size="small"
                                label={`Resubmit: ${resubmitStatus}`}
                                style={{
                                  width: "fit-content",
                                  fontWeight: 600,
                                  ...getResubmitStatusStyle(resubmitStatus),
                                }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell>
                            {canRequestResubmit ? (
                              <Button
                                variant="contained"
                                size="small"
                                style={{
                                  backgroundColor: "rgb(15, 59, 98)",
                                  color: "white",
                                }}
                                onClick={() => onRequestResubmit(event)}
                              >
                                Request Resubmit
                              </Button>
                            ) : isPastDue && isResubmitRequested(resubmitStatus) ? (
                              <Chip
                                size="small"
                                label="Requested"
                                style={{
                                  width: "fit-content",
                                  fontWeight: 600,
                                  ...getResubmitStatusStyle(resubmitStatus),
                                }}
                              />
                            ) : (
                              <Typography variant="caption" color="textSecondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Widget>
        </Grid>
      </Grid>
    </>
  );
}
